import { BehaviorSubject, delay, filter, map, merge, Observable, share, take, tap } from 'rxjs';
import { GridRowModel } from '@mui/x-data-grid/models/gridRows';
import { Pub, State, Sub } from '../../../state/state.abstract.ts';
import { GridRowParams } from '@mui/x-data-grid/models/params/gridRowParams';
import { GridEditCellValueParams } from '@mui/x-data-grid/models/params/gridEditCellParams';
import { TypeDictionaries } from '../dictionary.popup.tsx';
import { storageHelper } from '../../../helpers/storage';
import {
  CreateActions,
  DeleteActions,
  dictionaryActionsService,
  SaveActions,
} from '../services/dictionaryActions.service.ts';
import {
  handler as defaultErrorHandler,
  responseHandler,
} from '../../../responseHandler/responseHandler.ts';
import { C_Global_Error_Code } from '../../../../graphql/generatedModel.ts';
import { deletionConfirmationPopup } from '../deletionConfirmation/deletionConfirmation.instance.ts';
import { GridValidRowModel } from '@mui/x-data-grid-premium';

const initState: IDictionaryState = {
  action: undefined,
  selectedRow: undefined,
  updatingCell: undefined,
  data: [],
  limit: undefined,
  nameAlias: undefined,
  emptyRow: undefined,
  validateFields: undefined,
  dictionaryType: undefined,
  forceUnbindAndDelete: false,
};
class PubImpl extends Pub<IDictionaryState> {
  initData(params: TInitData) {
    this.emit('initData', { ...params });
  }
  selectRow(selectedRow: IDictionaryState['selectedRow']) {
    this.emit('selectRow', { selectedRow });
  }
  clearState() {
    this.emit('clearState', initState);
  }
  addRow(selectedRow: IDictionaryState['selectedRow']) {
    this.emit('addRow', { selectedRow });
  }
  deleteRow(flag?: IDictionaryState['forceUnbindAndDelete']) {
    this.emit('deleteRow', { forceUnbindAndDelete: !!flag });
  }
  updateCell(updatingCell: IDictionaryState['updatingCell'], parseFields?: boolean) {
    this.emit('updateCell', { updatingCell, parseFields });
  }
}
class SubImpl extends Sub<IDictionaryState> {
  private serviceMethodName?: string;
  private deletingRecordInUse = false;
  private disableDelete$ = new BehaviorSubject<boolean>(false);
  private disableEditing$ = new BehaviorSubject<string[]>([]);
  private createLock: Promise<void> = Promise.resolve();
  private inFlightCreateIds = new Set<string>();
  private shareDisableDelete$: Observable<boolean> = this.disableDelete$.pipe(share());
  private shareDisableEditing$: Observable<string[]> = this.disableEditing$.pipe(share());
  protected actionHandlers(): Observable<IDictionaryState> {
    return merge(this.addRow(), this.deleteRow(), this.updateCell(), this.updateState()).pipe(
      tap((state) => {
        if (state.action !== 'updateCell')
          this.stream$.next({ ...state, action: 'internalUpdate' });
      }),
    );
  }

  disableDelete() {
    return this.shareDisableDelete$;
  }
  disableEditing() {
    return this.shareDisableEditing$;
  }
  private addRow(): Observable<IDictionaryState> {
    return this.actionListener('addRow').pipe(
      map((state) => {
        const updatedState = this.customStructuredClone(state);
        const id = updatedState.limit
          ? `new_${this.findMissingId(updatedState.data)}`
          : `new_${Date.now()}`;
        updatedState.selectedRow = { ...updatedState.selectedRow, id };
        updatedState.data = [...updatedState.data, updatedState.selectedRow];
        return updatedState;
      }),
    );
  }

  private deleteRow(): Observable<IDictionaryState> {
    return this.actionListener('deleteRow').pipe(
      delay(50), // fixes the problem if the cell is in edit mode
      tap(({ forceUnbindAndDelete }) => {
        const { selectedRow, dictionaryType } = this.stream$.getValue();
        if (dictionaryType && (!selectedRow.invalid || !selectedRow.id.includes('new'))) {
          const deletionConfirmationPopupSub = deletionConfirmationPopup?.stream
            .state()
            .pipe(filter(({ action }) => action === 'confirm' || action === 'cancel'))
            .subscribe(({ action }) => {
              if (action === 'confirm') {
                dictionaryState.pub.deleteRow(true);
              }
              if (action === 'cancel') this.disableDelete$.next(false);
              this.deletingRecordInUse = false;
              deletionConfirmationPopupSub?.unsubscribe();
            });
          this.disableDelete$.next(true);
          (
            dictionaryActionsService.sub[
              `delete${this.serviceMethodName}` as DeleteActions
            ]() as Observable<any>
          )
            .pipe(
              responseHandler({
                quite: true,
                customErrorHandler: (data) => {
                  if (data.code === C_Global_Error_Code.GEC40_DELETING_RECORD_IN_USE) {
                    const linkedRecords = (data.linkedRecords || {}) as Record<string, any>;
                    deletionConfirmationPopup?.stream.emit('open', {
                      linkedRecords,
                      customMessage: '',
                      forbiddenToDelete: false,
                    });
                    this.deletingRecordInUse = true;
                  } else if (data.code === C_Global_Error_Code.GEC41_DELETE_PROHIBITED) {
                    deletionConfirmationPopup?.stream.emit('open', {
                      customMessage: 'settings.cannot_delete_this_bank',
                      forbiddenToDelete: true,
                    });
                    this.deletingRecordInUse = true;
                  } else defaultErrorHandler({ data });
                  return '';
                },
                success: () => 'common.successfully_deleted',
              }),
              take(1),
            )
            .subscribe((data) => {
              if (!this.deletingRecordInUse) {
                if (data) {
                  this._deleteRow();
                }
                this.disableDelete$.next(false);
                deletionConfirmationPopupSub?.unsubscribe();
              }
            });
          dictionaryActionsService.pub[`delete${this.serviceMethodName}` as DeleteActions]({
            id: selectedRow.id,
            forceUnbindAndDelete,
          });
        } else if (dictionaryType && selectedRow.invalid) this._deleteRow();
      }),
      filter(() => false),
    );
  }

  private updateCell(): Observable<IDictionaryState> {
    return this.actionListener('updateCell').pipe(
      map((state) => this.customStructuredClone(state)),
      map((state) => {
        const { id, field, value } = state.updatingCell!;
        const foundIndex = state.data.findIndex((el) => el.id === id);
        if (foundIndex !== -1) {
          if (state.parseFields) {
            state.data[foundIndex] = { ...state.selectedRow, ...value };
          } else {
            state.data[foundIndex][field] = value;

            if (state.validateFields) {
              let isInvalidRow = false;
              state.validateFields.forEach((validatedField) => {
                if (!isInvalidRow) {
                  if (typeof validatedField === 'function') {
                    isInvalidRow = !validatedField(state.data[foundIndex]);
                  } else {
                    const validatedValue = state.data[foundIndex][validatedField];
                    const isEmpty = !validatedValue;
                    const isNonUnique =
                      state.data.filter((row) => row[validatedField] === validatedValue).length > 1;
                    if (isEmpty || isNonUnique) {
                      isInvalidRow = true;
                    }
                  }
                }
              });
              if (isInvalidRow) {
                state.data[foundIndex].invalid = true;
              } else {
                delete state.data[foundIndex].invalid;
              }
            }
          }
          state.selectedRow = state.data[foundIndex];
        }
        this.stream$.next({ ...state, action: 'internalUpdate' });
        return state;
      }),
      tap(({ limit, selectedRow }) => {
        const newRow = selectedRow.id.includes('new');
        if (!newRow) this._updateCell();
        else this._createRow(limit);
      }),
    );
  }

  private _updateCell() {
    const { dictionaryType, selectedRow } = this.stream$.getValue();
    // Strip the placeholder marker — it's purely a client-side flag for
    // slot-mode rendering and must not leak into the backend payload
    // (GraphQL inputs like WA_PriceCategoryInput reject unknown fields).
    const { id, [DICT_SLOT_PLACEHOLDER_KEY]: _placeholder, ...fields } = selectedRow;
    if (dictionaryType && !selectedRow.invalid) {
      (
        dictionaryActionsService.sub[
          `save${this.serviceMethodName}` as SaveActions
        ]() as Observable<any>
      )
        .pipe(
          responseHandler({
            success: () => 'common.successfully_saved',
          }),
          take(1),
        )
        .subscribe();
      dictionaryActionsService.pub[`save${this.serviceMethodName}` as SaveActions]({
        id: String(id),
        fields,
      });

      storageHelper.memory.removeItem(`configsData.${dictionaryType}`);
    }
  }

  private _createRow(limit: IDictionaryState['limit']) {
    const { dictionaryType, selectedRow } = this.stream$.getValue();
    // Strip the placeholder marker — see _updateCell for the rationale.
    // The slot now carries a real value; the backend mutates the fixed
    // column (e.g. ArtikelPreisKategorie${id}) using id + fields only.
    const { id, [DICT_SLOT_PLACEHOLDER_KEY]: _placeholder, ...fields } = selectedRow;
    const limitedId = id.split('_')[1];
    const serviceMethodName = this.serviceMethodName;
    if (dictionaryType && !selectedRow.invalid) {
      // Deduplicate: skip if a create is already in-flight for this temp row
      if (this.inFlightCreateIds.has(id)) {
        return;
      }
      this.inFlightCreateIds.add(id);
      this.disableEditing$.next([...this.disableEditing$.value, id]);
      storageHelper.memory.removeItem(`configsData.${dictionaryType}`);

      this.createLock = this.createLock
        .catch(() => undefined)
        .then(
          () =>
            new Promise<void>((resolve) => {
              try {
                // Publish first so shareReplay(1) caches this row's action before we subscribe.
                // This prevents a concurrent subscriber from replaying a stale action.
                if (limit) {
                  dictionaryActionsService.pub[`save${serviceMethodName}` as SaveActions]({
                    id: limitedId,
                    fields,
                  });
                } else {
                  dictionaryActionsService.pub[`create${serviceMethodName}` as CreateActions]({
                    fields,
                  });
                }

                (limit
                  ? (dictionaryActionsService.sub[
                      `save${serviceMethodName}` as SaveActions
                    ]() as Observable<any>)
                  : (dictionaryActionsService.sub[
                      `create${serviceMethodName}` as CreateActions
                    ]() as Observable<any>)
                )
                  .pipe(
                    responseHandler({
                      success: () => 'common.successfully_saved',
                    }),
                    take(1),
                  )
                  .subscribe({
                    next: (data) => {
                      this.inFlightCreateIds.delete(selectedRow.id);
                      const updatedState = this.customStructuredClone(this.stream$.getValue());
                      this.disableEditing$.next(
                        this.disableEditing$.value.filter((item) => item !== selectedRow.id),
                      );
                      updatedState.data = updatedState.data.map((el) => {
                        if (el.id === selectedRow.id && (data?.id || limit)) {
                          // Drop the slot-mode placeholder marker — the slot
                          // is now filled, so subsequent clicks should select
                          // the row, not re-open it as an empty placeholder.
                          const { [DICT_SLOT_PLACEHOLDER_KEY]: _placeholder, ...rest } = el;
                          const updatedRow = { ...rest, id: limit ? limitedId : data.id };
                          updatedState.selectedRow = updatedRow;
                          return updatedRow;
                        }
                        return el;
                      });
                      this.stream$.next({ ...updatedState, action: 'updateState' });
                      resolve();
                    },
                    error: () => {
                      this.inFlightCreateIds.delete(selectedRow.id);
                      this.disableEditing$.next(
                        this.disableEditing$.value.filter((item) => item !== selectedRow.id),
                      );
                      resolve();
                    },
                  });
              } catch {
                this.inFlightCreateIds.delete(id);
                this.disableEditing$.next(this.disableEditing$.value.filter((item) => item !== id));
                resolve();
              }
            }),
        );
    }
  }

  _deleteRow() {
    const updatedState = this.customStructuredClone(this.stream$.getValue());
    const { limit, selectedRow: deleted, nameAlias, emptyRow } = updatedState;
    if (limit) {
      // Limited list (slot-mode): the deleted row's slot must remain visible
      // as an empty placeholder at the same position, not collapse away.
      // Otherwise the user sees a "row disappeared" instead of "slot 3 is now
      // free" — which is exactly the UX confusion WA-3506 is fixing.
      //
      // Use slotIndex() (not Number()) so this also handles the unsaved-row
      // case where deleted.id is still in the temporary "new_<slot>" form
      // (e.g. user types into an empty slot, then deletes before save
      // commits). Number("new_3") is NaN and would corrupt the grid with
      // id: "new_NaN".
      updatedState.data = updatedState.data.map((row) =>
        row?.id === deleted?.id
          ? buildPlaceholderRow(slotIndex(deleted!), nameAlias || 'name', emptyRow)
          : row,
      );
      updatedState.selectedRow = undefined;
    } else {
      let selectedRow: IDictionaryState['selectedRow'];
      updatedState.data = updatedState.data.filter((pos, i, arr) => {
        if (pos?.id === deleted?.id) {
          const nextRow = arr?.[i + 1];
          const prevRow = arr?.[i - 1];
          if (i === 0 && arr!.length > 1) {
            selectedRow = nextRow;
          }
          if (i !== 0) {
            selectedRow = prevRow;
          }
          return false;
        } else return true;
      });
      updatedState.selectedRow = selectedRow;
    }
    this.stream$.next({ ...updatedState, action: 'updateState' });
    storageHelper.memory.removeItem(`configsData.${updatedState.dictionaryType!}`);
  }

  private updateState() {
    return this.actionListener(['initData', 'selectRow', 'updateState', 'clearState']).pipe(
      tap(({ dictionaryType, action }) => {
        if (dictionaryType && action === 'initData') {
          this.serviceMethodName = dictionaryType[0].toUpperCase() + dictionaryType.slice(1);

          // Establish shareReplay connection so pub-first creates never lose their emission.
          dictionaryActionsService.sub.connect();
        }
        if (action === 'clearState') {
          this.disableDelete$.next(false);
          this.disableEditing$.next([]);
          this.inFlightCreateIds.clear();
          this.createLock = Promise.resolve();
        }
      }),
    );
  }

  /* UTILITY start */
  private findMissingId(arr: IDictionaryState['data']) {
    const existingIds = arr.map((item) => {
      if (item.id.includes('new_')) {
        const clearId = item.id.split('_')[1];
        return Number(clearId);
      }
      return Number(item.id);
    });
    for (let i = 1; i <= existingIds.length + 1; i++) {
      if (!existingIds.includes(i)) {
        return String(i);
      }
    }
    return String(existingIds.length + 1);
  }
  private customStructuredClone(state: IDictionaryState): IDictionaryState {
    const { validateFields, ...restState } = state;
    const copyState = structuredClone(restState);
    return { validateFields, ...copyState };
  }
  /* UTILITY end */
}

class DictionaryState extends State<IDictionaryState> {
  pub = new PubImpl(this.stream$);
  sub = new SubImpl(this.stream$);
}

export const dictionaryState = new DictionaryState(initState);

/**
 * Marker key applied to placeholder rows in limited (slot-mode) dictionaries.
 * Exported so the UI layer (renderCell, onRowClick, viewRows) can recognise
 * empty slot rows without depending on heuristics like "id starts with new_".
 *
 * See WA-3506: in limited lists (PriceCategory, DiscountGroup, DeliveryNoteGroup
 * etc.) the dictionary maps to a fixed set of N columns in
 * FakturaAssistKonfiguration; we render all N slot positions and mark unused
 * ones as placeholders so the user can fill a specific slot directly instead
 * of relying on the implicit "first free slot" behaviour the customer
 * mistook for silent renumbering.
 */
export const DICT_SLOT_PLACEHOLDER_KEY = '__isEmpty' as const;

export function buildPlaceholderRow(
  slotId: number,
  nameAlias: string,
  emptyRow?: GridRowParams['row'],
): GridRowModel {
  return {
    ...(emptyRow ?? {}),
    [nameAlias]: null,
    id: `new_${slotId}`,
    [DICT_SLOT_PLACEHOLDER_KEY]: true,
  };
}

/**
 * Take the filled rows returned by the backend (only slots whose name is set)
 * and pad them with placeholder rows for every unused slot, sorted by slot id
 * ascending so the rendered order matches the legacy WinAssist UI (slots 1..N
 * in fixed positions).
 */
export function padWithEmptySlots(
  filledRows: GridRowModel[],
  limit: number,
  nameAlias: string,
  emptyRow?: GridRowParams['row'],
): GridRowModel[] {
  const filledIds = new Set(filledRows.map((r) => Number(r.id)));
  const placeholders: GridRowModel[] = [];
  for (let slotId = 1; slotId <= limit; slotId++) {
    if (!filledIds.has(slotId)) {
      placeholders.push(buildPlaceholderRow(slotId, nameAlias, emptyRow));
    }
  }
  return [...filledRows, ...placeholders].sort((a, b) => slotIndex(a) - slotIndex(b));
}

function slotIndex(row: GridRowModel): number {
  const raw = String(row.id ?? '');
  return Number(raw.startsWith('new_') ? raw.slice(4) : raw);
}

export interface IDictionaryState {
  action:
    | undefined
    | 'initData'
    | 'selectRow'
    | 'addRow'
    | 'deleteRow'
    | 'updateCell'
    | 'clearState'
    | 'internalUpdate'
    | 'updateState';
  data: GridRowModel[];
  selectedRow: GridRowParams['row'];
  updatingCell: GridEditCellValueParams | undefined;
  parseFields?: boolean; // we use this argument {{parseFields}} to save all the fields passed in instead of a specific field name //
  /**
   * Slot count for limited (slot-based) dictionaries (PriceCategory=10,
   * DiscountGroup=20, etc.). Undefined for regular unlimited lists.
   * Truthy when the popup operates in slot-mode.
   */
  limit: number | undefined;
  /** Field name that carries the human-readable label; default 'name'. */
  nameAlias?: string;
  /** Per-dictionary default values for newly opened empty rows. */
  emptyRow?: GridRowParams['row'];
  validateFields?: Array<string | ((row: GridValidRowModel) => boolean)>;
  dictionaryType?: TypeDictionaries;
  forceUnbindAndDelete?: boolean;
}

type TInitData = Pick<
  IDictionaryState,
  'data' | 'limit' | 'nameAlias' | 'emptyRow' | 'validateFields' | 'dictionaryType'
>;
